import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  FiSearch,
  FiRefreshCw,
  FiLayers,
  FiVideo,
  FiMessageSquare,
  FiGlobe,
} from 'react-icons/fi';
import AdminSidebar from './AdminSidebar';
import DashboardHeader from './DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import {
  structuralSegmentsAPI,
  adminPodcastsAPI,
  adminDialogueCategoriesAPI,
  StructuralSegment,
  Podcast,
  CategoryDialogue,
  authAPI,
} from '../services/api';
import './PublicAccessManagement.css';
import './Dashboard.css';

type TabId = 'segments' | 'podcasts' | 'dialogues';

interface PublicAccessManagementProps {
  onLogout?: () => void;
  userEmail?: string;
}

const PublicAccessManagement: React.FC<PublicAccessManagementProps> = ({
  onLogout,
  userEmail,
}) => {
  const { isCollapsed } = useSidebar();
  const [userInfo, setUserInfo] = useState({
    email: userEmail || '',
    organization: 'NAATI',
    first_name: '',
  });
  const [activeTab, setActiveTab] = useState<TabId>('segments');
  const [searchTerm, setSearchTerm] = useState('');
  const [segments, setSegments] = useState<StructuralSegment[]>([]);
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [categoryDialogues, setCategoryDialogues] = useState<CategoryDialogue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const fetchUserProfile = useCallback(async () => {
    try {
      const profile = await authAPI.getProfile();
      setUserInfo({
        email: profile.email || userEmail || '',
        organization: profile.organization || 'NAATI',
        first_name: profile.first_name || '',
      });
    } catch (e) {
      console.error(e);
    }
  }, [userEmail]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [seg, pod, cat] = await Promise.all([
        structuralSegmentsAPI.getSegments({ page: 1, page_size: 500 }),
        adminPodcastsAPI.listPodcasts({ page: 1, page_size: 500 }),
        adminDialogueCategoriesAPI.getCategories(),
      ]);
      setSegments(seg.results);
      setPodcasts(pod.results);
      const dialogueLists = await Promise.all(
        cat.map((c) => adminDialogueCategoriesAPI.getDialogues(c.id, { page: 1, page_size: 500 }))
      );
      setCategoryDialogues(dialogueLists.flatMap((d) => d.results));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserProfile();
    loadAll();
  }, [fetchUserProfile, loadAll]);

  const filteredSegments = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return segments;
    return segments.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.test_category && s.test_category.toLowerCase().includes(q)) ||
        String(s.segment_number ?? '').includes(q)
    );
  }, [segments, searchTerm]);

  const filteredPodcasts = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return podcasts;
    return podcasts.filter(
      (p) =>
        p.name.toLowerCase().includes(q) ||
        (p.description && p.description.toLowerCase().includes(q))
    );
  }, [podcasts, searchTerm]);

  const filteredCategoryDialogues = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return categoryDialogues;
    return categoryDialogues.filter(
      (d) =>
        d.name.toLowerCase().includes(q) ||
        (d.description && d.description.toLowerCase().includes(q)) ||
        (d.category_name && d.category_name.toLowerCase().includes(q))
    );
  }, [categoryDialogues, searchTerm]);

  const freeCounts = useMemo(
    () => ({
      segments: segments.filter((s) => s.is_free_public).length,
      podcasts: podcasts.filter((p) => p.is_free_public).length,
      dialogues: categoryDialogues.filter((d) => d.is_free_public).length,
    }),
    [segments, podcasts, categoryDialogues]
  );

  const toggleSegment = async (id: number, next: boolean) => {
    const key = `seg-${id}`;
    setSavingId(key);
    setSuccess(null);
    setError(null);
    try {
      const updated = await structuralSegmentsAPI.updateSegment(id, {
        is_free_public: next,
      });
      setSegments((prev) => prev.map((s) => (s.id === id ? updated : s)));
      setSuccess(next ? 'Marked as free for everyone.' : 'Marked as subscription-only.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const togglePodcast = async (id: number, next: boolean) => {
    const key = `pod-${id}`;
    setSavingId(key);
    setSuccess(null);
    setError(null);
    try {
      const updated = await adminPodcastsAPI.updatePodcast(id, {
        is_free_public: next,
      });
      setPodcasts((prev) => prev.map((p) => (p.id === id ? updated : p)));
      setSuccess(next ? 'Podcast is now free for everyone.' : 'Podcast requires subscription.');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const toggleCategoryDialogue = async (id: number, next: boolean) => {
    const key = `dlg-${id}`;
    setSavingId(key);
    setSuccess(null);
    setError(null);
    try {
      const updated = await adminDialogueCategoriesAPI.updateDialogue(id, {
        is_free_public: next,
      });
      setCategoryDialogues((prev) => prev.map((d) => (d.id === id ? updated : d)));
      setSuccess(
        next
          ? 'This dialogue is now free for everyone without a subscription.'
          : 'This dialogue now requires a subscription (unless opened another way).'
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const tabs: { id: TabId; label: string; icon: typeof FiLayers; count: number; free: number }[] =
    [
      {
        id: 'dialogues',
        label: 'Dialogue categories',
        icon: FiMessageSquare,
        count: categoryDialogues.length,
        free: freeCounts.dialogues,
      },
      {
        id: 'segments',
        label: 'Structural segments',
        icon: FiLayers,
        count: segments.length,
        free: freeCounts.segments,
      },
      {
        id: 'podcasts',
        label: 'Podcasts',
        icon: FiVideo,
        count: podcasts.length,
        free: freeCounts.podcasts,
      },
    ];

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <AdminSidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        <div className="dashboard-content">
          <div className="dashboard-content-inner public-access-page">
            <motion.div
              className="pa-header"
              initial={{ opacity: 0, y: -12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <div className="pa-header-text">
                <div className="pa-header-icon">
                  {React.createElement(FiGlobe as React.ComponentType<{ size?: number }>, {
                    size: 28,
                  })}
                </div>
                <div>
                  <h1 className="dashboard-title">Public access</h1>
                  <p className="dashboard-subtitle">
                    Choose which structural segments, podcasts, and individual dialogues (under Dialogue
                    Categories) are available to all signed-in users <strong>without</strong> a subscription.
                    Mock service and other areas stay subscription-only.
                  </p>
                </div>
              </div>
              <motion.button
                type="button"
                className="pa-refresh-btn"
                onClick={() => loadAll()}
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>, {
                  className: loading ? 'pa-spin' : '',
                  size: 18,
                })}
                Refresh
              </motion.button>
            </motion.div>

            <div className="pa-stats">
              {tabs.map((t) => (
                <div key={t.id} className="pa-stat-card">
                  <span className="pa-stat-label">{t.label}</span>
                  <span className="pa-stat-value">
                    {t.free} <span className="pa-stat-muted">/ {t.count} free</span>
                  </span>
                </div>
              ))}
            </div>

            {error && (
              <motion.div
                className="pa-message pa-message-error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                role="alert"
              >
                {error}
                <button type="button" className="pa-dismiss" onClick={() => setError(null)}>
                  ×
                </button>
              </motion.div>
            )}
            {success && (
              <motion.div
                className="pa-message pa-message-success"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {success}
                <button type="button" className="pa-dismiss" onClick={() => setSuccess(null)}>
                  ×
                </button>
              </motion.div>
            )}

            <div className="pa-tabs" role="tablist">
              {tabs.map((t) => {
                const Icon = t.icon;
                const active = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    className={`pa-tab ${active ? 'pa-tab-active' : ''}`}
                    onClick={() => {
                      setActiveTab(t.id);
                      setSearchTerm('');
                    }}
                  >
                    {React.createElement(Icon as React.ComponentType<{ size?: number }>, { size: 18 })}
                    {t.label}
                    <span className="pa-tab-badge">{t.free}</span>
                  </button>
                );
              })}
            </div>

            <div className="pa-toolbar">
              <div className="pa-search">
                {React.createElement(FiSearch as React.ComponentType<{ size?: number }>, { size: 18 })}
                <input
                  type="search"
                  placeholder={
                    activeTab === 'segments'
                      ? 'Search segments…'
                      : activeTab === 'podcasts'
                        ? 'Search podcasts…'
                        : 'Search dialogues…'
                  }
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="pa-panel">
              {loading ? (
                <div className="pa-loading">
                  {React.createElement(
                    FiRefreshCw as React.ComponentType<{ className?: string; size?: number }>,
                    { className: 'pa-spin', size: 32 }
                  )}
                  <p>Loading…</p>
                </div>
              ) : activeTab === 'segments' ? (
                <div className="pa-table-wrap">
                  <table className="pa-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Category / #</th>
                        <th>Status</th>
                        <th>Free for everyone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSegments.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="pa-empty">
                            No segments match your search.
                          </td>
                        </tr>
                      ) : (
                        filteredSegments.map((s) => (
                          <tr key={s.id}>
                            <td className="pa-cell-name">{s.name}</td>
                            <td className="pa-cell-meta">
                              {s.test_category || '—'}
                              {s.segment_number != null && (
                                <span className="pa-pill">Seg {s.segment_number}</span>
                              )}
                            </td>
                            <td>
                              <span className={s.is_active ? 'pa-badge pa-badge-on' : 'pa-badge pa-badge-off'}>
                                {s.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <label className="pa-switch">
                                <input
                                  type="checkbox"
                                  checked={Boolean(s.is_free_public)}
                                  disabled={savingId === `seg-${s.id}`}
                                  onChange={(e) => toggleSegment(s.id, e.target.checked)}
                                />
                                <span className="pa-switch-slider" />
                              </label>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : activeTab === 'podcasts' ? (
                <div className="pa-table-wrap">
                  <table className="pa-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Free for everyone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPodcasts.length === 0 ? (
                        <tr>
                          <td colSpan={3} className="pa-empty">
                            No podcasts match your search.
                          </td>
                        </tr>
                      ) : (
                        filteredPodcasts.map((p) => (
                          <tr key={p.id}>
                            <td className="pa-cell-name">{p.name}</td>
                            <td>
                              <span className={p.is_active ? 'pa-badge pa-badge-on' : 'pa-badge pa-badge-off'}>
                                {p.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <label className="pa-switch">
                                <input
                                  type="checkbox"
                                  checked={Boolean(p.is_free_public)}
                                  disabled={savingId === `pod-${p.id}`}
                                  onChange={(e) => togglePodcast(p.id, e.target.checked)}
                                />
                                <span className="pa-switch-slider" />
                              </label>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="pa-table-wrap">
                  <table className="pa-table">
                    <thead>
                      <tr>
                        <th>Dialogue</th>
                        <th>Category</th>
                        <th>Status</th>
                        <th>Free for everyone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredCategoryDialogues.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="pa-empty">
                            No dialogues match your search.
                          </td>
                        </tr>
                      ) : (
                        filteredCategoryDialogues.map((d) => (
                          <tr key={d.id}>
                            <td className="pa-cell-name">{d.name}</td>
                            <td className="pa-cell-meta">{d.category_name || '—'}</td>
                            <td>
                              <span className={d.is_active ? 'pa-badge pa-badge-on' : 'pa-badge pa-badge-off'}>
                                {d.is_active ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td>
                              <label className="pa-switch">
                                <input
                                  type="checkbox"
                                  checked={Boolean(d.is_free_public)}
                                  disabled={savingId === `dlg-${d.id}`}
                                  onChange={(e) => toggleCategoryDialogue(d.id, e.target.checked)}
                                />
                                <span className="pa-switch-slider" />
                              </label>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicAccessManagement;
